import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  QUERY_KEY_NOVELTIES_BEST_CANDIDATES,
  QUERY_KEY_SKU_FORECAST_DATA,
} from 'src/keys';
import { useUpsertProductsSalesPotential } from 'src/services/api/upsertProductsSalesPotential';

type UseSalesPotentialEditorParams = {
  sku: string;
  salesPotential: number | null;
};

export function useSalesPotentialEditor({
  sku,
  salesPotential,
}: UseSalesPotentialEditorParams) {
  const { t } = useTranslation('novelties');
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(salesPotential?.toString() ?? '');

  const { mutate: upsertSalesPotential, isPending } =
    useUpsertProductsSalesPotential();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditValue(salesPotential?.toString() ?? '');
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(false);
    setEditValue(salesPotential?.toString() ?? '');
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const value = parseFloat(editValue);

    if (isNaN(value) || value < 0) {
      toast.error(t('Error'), {
        description: t('Please enter a valid positive number'),
      });
      return;
    }

    upsertSalesPotential(
      { sku, sales_potential: value },
      {
        onSuccess: () => {
          setIsEditing(false);
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_NOVELTIES_BEST_CANDIDATES],
          });
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY_SKU_FORECAST_DATA, sku],
          });
          toast.success(t('Success'), {
            description: t('Sales potential updated successfully'),
          });
        },
        onError: () => {
          toast.error(t('Error'), {
            description: t('Failed to update sales potential'),
          });
        },
      },
    );
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave(e as unknown as React.MouseEvent);
    } else if (e.key === 'Escape') {
      handleCancel(e as unknown as React.MouseEvent);
    }
  };

  return {
    isEditing,
    editValue,
    isPending,
    setEditValue,
    handleEdit,
    handleCancel,
    handleSave,
    handleInputClick,
    handleKeyDown,
  };
}
